// angular

import {
    Component,
    forwardRef,
    HostBinding,
    Input,
    Output,
    EventEmitter,
    Renderer2,
    ViewChild,
    ViewChildren,
    ContentChildren,
    ContentChild,
    OnInit,
    TemplateRef,
    QueryList,
    AfterViewInit,
    Type
} from '@angular/core';

import {
    AsyncValidatorFn,
    FormControl,
    NG_VALUE_ACCESSOR,
    ValidatorFn
} from '@angular/forms';

// rx

import { Observable } from 'rxjs/Observable';
import'rxjs/add/observable/of';
import'rxjs/add/operator/debounceTime';
import'rxjs/add/operator/filter';
import'rxjs/add/operator/map';
import'rxjs/add/operator/first';

// ng2-tag-input

import { TagInputAccessor, TagModel } from '../../core/accessor';
import { listen } from '../../core/helpers/listen';
import * as constants from '../../core/constants';

import { DragProvider, DraggedTag } from '../../core/providers/drag-provider';
import { OptionsProvider } from '../../core/providers/options-provider';

import { TagInputForm } from '../tag-input-form/tag-input-form.component';
import { TagInputDropdown } from '../dropdown/tag-input-dropdown.component';
import { TagComponent } from '../tag/tag.component';

import { animations } from './animations';
import { TagInputOptions } from '../../defaults';

// angular universal hacks
/* tslint:disable-next-line */
const /** @type {?} */ DragEvent = ( /** @type {?} */((global as any))).DragEvent;

const /** @type {?} */ CUSTOM_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TagInputComponent),
    multi: true
};

const /** @type {?} */ defaults: Type<TagInputOptions> = forwardRef(() => OptionsProvider.defaults.tagInput);
export class TagInputComponent extends TagInputAccessor implements OnInit, AfterViewInit {
/**
 * \@name separatorKeys
 * @desc keyboard keys with which a user can separate items
 */
public separatorKeys: string[] = new defaults().separatorKeys;
/**
 * \@name separatorKeyCodes
 * @desc keyboard key codes with which a user can separate items
 */
public separatorKeyCodes: number[] = new defaults().separatorKeyCodes;
/**
 * \@name placeholder
 * @desc the placeholder of the input text
 */
public placeholder: string = new defaults().placeholder;
/**
 * \@name secondaryPlaceholder
 * @desc placeholder to appear when the input is empty
 */
public secondaryPlaceholder: string = new defaults().secondaryPlaceholder;
/**
 * \@name maxItems
 * @desc maximum number of items that can be added
 */
public maxItems: number = new defaults().maxItems;
/**
 * \@name validators
 * @desc array of Validators that are used to validate the tag before it gets appended to the list
 */
public validators: ValidatorFn[] = new defaults().validators;
/**
 * \@name asyncValidators
 * @desc array of AsyncValidator that are used to validate the tag before it gets appended to the list
 */
public asyncValidators: AsyncValidatorFn[] = new defaults().asyncValidators;
/**
 * - if set to true, it will only possible to add items from the autocomplete
 * \@name onlyFromAutocomplete
 */
public onlyFromAutocomplete = new defaults().onlyFromAutocomplete;
/**
 * \@name errorMessages
 */
public errorMessages: { [key: string]: string } = new defaults().errorMessages;
/**
 * \@name theme
 */
public theme: string = new defaults().theme;
/**
 * \@name onTextChangeDebounce
 */
public onTextChangeDebounce = new defaults().onTextChangeDebounce;
/**
 * - custom id assigned to the input
 * \@name id
 */
public inputId: string = new defaults().inputId;
/**
 * - custom class assigned to the input
 */
public inputClass: string = new defaults().inputClass;
/**
 * - option to clear text input when the form is blurred
 * \@name clearOnBlur
 */
public clearOnBlur: boolean = new defaults().clearOnBlur;
/**
 * - hideForm
 * \@name clearOnBlur
 */
public hideForm: boolean = new defaults().hideForm;
/**
 * \@name addOnBlur
 */
public addOnBlur: boolean = new defaults().addOnBlur;
/**
 * \@name addOnPaste
 */
public addOnPaste: boolean = new defaults().addOnPaste;
/**
 * - pattern used with the native method split() to separate patterns in the string pasted
 * \@name pasteSplitPattern
 */
public pasteSplitPattern = new defaults().pasteSplitPattern;
/**
 * \@name blinkIfDupe
 */
public blinkIfDupe = new defaults().blinkIfDupe;
/**
 * \@name removable
 */
public removable = new defaults().removable;
/**
 * \@name editable
 */
public editable: boolean = new defaults().editable;
/**
 * \@name allowDupes
 */
public allowDupes = new defaults().allowDupes;
/**
 * \@description if set to true, the newly added tags will be added as strings, and not objects
 * \@name modelAsStrings
 */
public modelAsStrings = new defaults().modelAsStrings;
/**
 * \@name trimTags
 */
public trimTags = new defaults().trimTags;
/**
 * \@name inputText
 * @return {?}
 */
public get inputText(): string {
        return this.inputTextValue;
    }
/**
 * \@name ripple
 */
public ripple: boolean = new defaults().ripple;
/**
 * \@name tabindex
 * @desc pass through the specified tabindex to the input
 */
public tabindex: string = new defaults().tabIndex;
/**
 * \@name disable
 */
public disable: boolean = new defaults().disable;
/**
 * \@name dragZone
 */
public dragZone: string = new defaults().dragZone;
/**
 * \@name onRemoving
 */
public onRemoving = new defaults().onRemoving;
/**
 * \@name onAdding
 */
public onAdding = new defaults().onAdding;
/**
 * \@name animationDuration
 */
public animationDuration = new defaults().animationDuration;
/**
 * \@name onAdd
 * @desc event emitted when adding a new item
 */
public onAdd = new EventEmitter<TagModel>();
/**
 * \@name onRemove
 * @desc event emitted when removing an existing item
 */
public onRemove = new EventEmitter<TagModel>();
/**
 * \@name onSelect
 * @desc event emitted when selecting an item
 */
public onSelect = new EventEmitter<TagModel>();
/**
 * \@name onFocus
 * @desc event emitted when the input is focused
 */
public onFocus = new EventEmitter<string>();
/**
 * \@name onFocus
 * @desc event emitted when the input is blurred
 */
public onBlur = new EventEmitter<string>();
/**
 * \@name onTextChange
 * @desc event emitted when the input value changes
 */
public onTextChange = new EventEmitter<TagModel>();
/**
 * - output triggered when text is pasted in the form
 * \@name onPaste
 */
public onPaste = new EventEmitter<string>();
/**
 * - output triggered when tag entered is not valid
 * \@name onValidationError
 */
public onValidationError = new EventEmitter<TagModel>();
/**
 * - output triggered when tag is edited
 * \@name onTagEdited
 */
public onTagEdited = new EventEmitter<TagModel>();
/**
 * \@name dropdown
 */
public dropdown: TagInputDropdown;
/**
 * \@name template
 * @desc reference to the template if provided by the user
 */
public templates: QueryList<TemplateRef<any>>;
/**
 * \@name inputForm
 */
public inputForm: TagInputForm;
/**
 * \@name selectedTag
 * @desc reference to the current selected tag
 */
public selectedTag: TagModel | undefined;
/**
 * \@name isLoading
 */
public isLoading = false;
/**
 * \@name inputText
 * @param {?} text
 * @return {?}
 */
public set inputText(text: string) {
        this.inputTextValue = text;
        this.inputTextChange.emit(text);
    }
/**
 * \@name tags
 * @desc list of Element items
 */
public tags: QueryList<TagComponent>;
/**
 * \@name listeners
 * @desc array of events that get fired using \@fireEvents
 */
private listeners = {
        [constants.KEYDOWN]: <{ (fun): any }[]>[],
        [constants.KEYUP]: <{ (fun): any }[]>[]
    };
/**
 * \@description emitter for the 2-way data binding inputText value
 * \@name inputTextChange
 */
public inputTextChange: EventEmitter<string> = new EventEmitter();
/**
 * \@description private variable to bind get/set
 * \@name inputTextValue
 */
public inputTextValue = '';
/**
 * @desc removes the tab index if it is set - it will be passed through to the input
 * \@name tabindexAttr
 * @return {?}
 */
public get tabindexAttr(): string {
        return this.tabindex !== '' ? '-1' : '';
    }
/**
 * \@name animationMetadata
 */
public animationMetadata: { value: string, params: object };
/**
 * @param {?} renderer
 * @param {?} dragProvider
 */
constructor(private readonly renderer: Renderer2,
public readonly dragProvider: DragProvider) {
        super();
    }
/**
 * \@name ngAfterViewInit
 * @return {?}
 */
public ngAfterViewInit(): void {
        // set up listeners

        this.setUpKeypressListeners();
        this.setupSeparatorKeysListener();
        this.setUpInputKeydownListeners();

        if (this.onTextChange.observers.length) {
            this.setUpTextChangeSubscriber();
        }

        // if clear on blur is set to true, subscribe to the event and clear the text's form
        if (this.clearOnBlur || this.addOnBlur) {
            this.setUpOnBlurSubscriber();
        }

        // if addOnPaste is set to true, register the handler and add items
        if (this.addOnPaste) {
            this.setUpOnPasteListener();
        }

        // if hideForm is set to true, remove the input
        if (this.hideForm) {
            this.inputForm.destroy();
        }
    }
/**
 * \@name ngOnInit
 * @return {?}
 */
public ngOnInit(): void {
        // if the number of items specified in the model is > of the value of maxItems
        // degrade gracefully and let the max number of items to be the number of items in the model
        // though, warn the user.
        const /** @type {?} */ hasReachedMaxItems = this.maxItems !== undefined &&
            this.items &&
            this.items.length > this.maxItems;

        if (hasReachedMaxItems) {
            this.maxItems = this.items.length;
            console.warn(constants.MAX_ITEMS_WARNING);
        }

	    // Setting editable to false to fix problem with tags in IE still being editable when
	    // onlyFromAutocomplete is true
		this.editable = this.onlyFromAutocomplete ? false : this.editable;

        this.setAnimationMetadata();
    }
/**
 * \@name onRemoveRequested
 * @param {?} tag
 * @param {?} index
 * @return {?}
 */
public onRemoveRequested(tag: TagModel, index: number): void {
        const /** @type {?} */ subscribeFn = (model: TagModel) => this.removeItem(model, index);

        this.onRemoving ?
            this.onRemoving(tag)
                .first()
                .subscribe(subscribeFn) : subscribeFn(tag);
    }
/**
 * \@name onAddingRequested
 * @param {?} fromAutocomplete {boolean}
 * @param {?} tag {TagModel}
 * @param {?=} index
 * @return {?}
 */
public onAddingRequested(fromAutocomplete: boolean, tag: TagModel, index?: number): void {
        if (!tag) {
            return;
        }

        const /** @type {?} */ subscribeFn = (model: TagModel) => {
            return this.addItem(fromAutocomplete, model, index);
        }

        this.onAdding ?
            this.onAdding(tag)
                .first()
                .subscribe(subscribeFn) : subscribeFn(tag);
    }
/**
 * \@name appendTag
 * @param tag {TagModel}
 */
public appendTag = (tag: TagModel, index = this.items.length): void => {
        const items = this.items;
        const model = this.modelAsStrings ? tag[this.identifyBy] : tag;

        this.items = [
            ...items.slice(0, index),
            model,
            ...items.slice(index, items.length)
        ];
    }
/**
 * \@name createTag
 * @param model
 */
public createTag = (model: TagModel): TagModel => {
        const trim = (val: TagModel, key: string): TagModel => {
            return typeof val === 'string' ? val.trim() : val[key];
        };

        return {
            ...typeof model !== 'string' ? model : {},
            [this.displayBy]: this.trimTags ? trim(model, this.displayBy) : model,
            [this.identifyBy]: this.trimTags ? trim(model, this.identifyBy) : model
        };
    }
/**
 * \@name selectItem
 * @desc selects item passed as parameter as the selected tag
 * @param {?} item
 * @param {?=} emit
 * @return {?}
 */
public selectItem(item: TagModel | undefined, emit = true): void {
        const /** @type {?} */ isReadonly = item && typeof item !== 'string' && item.readonly;

        if (isReadonly || this.selectedTag === item) {
            return;
        }

        this.selectedTag = item;

        if (emit) {
            this.onSelect.emit(item);
        }
    }
/**
 * \@name fireEvents
 * @desc goes through the list of the events for a given eventName, and fires each of them
 * @param {?} eventName
 * @param {?=} $event
 * @return {?}
 */
public fireEvents(eventName: string, $event?): void {
        this.listeners[eventName].forEach(listener => listener.call(this, $event));
    }
/**
 * \@name handleKeydown
 * @desc handles action when the user hits a keyboard key
 * @param {?} data
 * @return {?}
 */
public handleKeydown(data: any): void {
        const /** @type {?} */ event = data.event;
        const /** @type {?} */ key = event.keyCode || event.which;

        switch (constants.KEY_PRESS_ACTIONS[key]) {
            case constants.ACTIONS_KEYS.DELETE:
                if (this.selectedTag && this.removable) {
                    const /** @type {?} */ index = this.items.indexOf(this.selectedTag);
                    this.onRemoveRequested(this.selectedTag, index);
                }
                break;
            case constants.ACTIONS_KEYS.SWITCH_PREV:
                this.moveToTag(data.model, constants.PREV);
                break;
            case constants.ACTIONS_KEYS.SWITCH_NEXT:
                this.moveToTag(data.model, constants.NEXT);
                break;
            case constants.ACTIONS_KEYS.TAB:
                this.moveToTag(data.model, constants.NEXT);
                break;
            default:
                return;
        }

        // prevent default behaviour
        event.preventDefault();
    }
/**
 * \@name setInputValue
 * @param {?} value
 * @return {?}
 */
public setInputValue(value: string): void {
        const /** @type {?} */ control = this.getControl();

        // update form value with the transformed item
        control.setValue(value);
    }
/**
 * \@name getControl
 * @return {?}
 */
private getControl(): FormControl {
        return /** @type {?} */(( <FormControl>this.inputForm.value));
    }
/**
 * \@name focus
 * @param {?=} applyFocus
 * @param {?=} displayAutocomplete
 * @return {?}
 */
public focus(applyFocus = false, displayAutocomplete = false): void {
        if (this.dragProvider.getState('dragging')) {
            return;
        }

        this.selectItem(undefined, false);

        if (applyFocus) {
            this.inputForm.focus();
            this.onFocus.emit(this.formValue);
        }
    }
/**
 * \@name blur
 * @return {?}
 */
public blur(): void {
        this.onTouched();

        this.onBlur.emit(this.formValue);
    }
/**
 * \@name hasErrors
 * @return {?}
 */
public hasErrors(): boolean {
        return this.inputForm && this.inputForm.hasErrors();
    }
/**
 * \@name isInputFocused
 * @return {?}
 */
public isInputFocused(): boolean {
        return this.inputForm && this.inputForm.isInputFocused();
    }
/**
 * - this is the one way I found to tell if the template has been passed and it is not
 * the template for the menu item
 * \@name hasCustomTemplate
 * @return {?}
 */
public hasCustomTemplate(): boolean {
        const /** @type {?} */ template = this.templates ? this.templates.first : undefined;
        const /** @type {?} */ menuTemplate = this.dropdown && this.dropdown.templates ?
            this.dropdown.templates.first : undefined;

        return Boolean(template && template !== menuTemplate);
    }
/**
 * \@name maxItemsReached
 * @return {?}
 */
public get maxItemsReached(): boolean {
        return this.maxItems !== undefined &&
            this.items.length >= this.maxItems;
    }
/**
 * \@name formValue
 * @return {?}
 */
public get formValue(): string {
        const /** @type {?} */ form = this.inputForm.value;

        return form ? form.value : '';
    }
/**
 * 3
 * \@name onDragStarted
 * @param {?} event
 * @param {?} tag
 * @param {?} index
 * @return {?}
 */
public onDragStarted(event: DragEvent, tag: TagModel, index: number): void {
        event.stopPropagation();

        const /** @type {?} */ item = /** @type {?} */(( { zone: this.dragZone, tag, index } as DraggedTag));

        this.dragProvider.setSender(this);
        this.dragProvider.setDraggedItem(event, item);
        this.dragProvider.setState({dragging: true, index});
    }
/**
 * \@name onDragOver
 * @param {?} event
 * @param {?=} index
 * @return {?}
 */
public onDragOver(event: DragEvent, index?: number): void {
        this.dragProvider.setState({dropping: true});
        this.dragProvider.setReceiver(this);

        event.preventDefault();
    }
/**
 * \@name onTagDropped
 * @param {?} event
 * @param {?} index
 * @return {?}
 */
public onTagDropped(event: DragEvent, index: number): void {
        const /** @type {?} */ item = this.dragProvider.getDraggedItem(event);

        if (item.zone !== this.dragZone) {
            return;
        }

        this.dragProvider.onTagDropped(item.tag, item.index, index);

        event.preventDefault();
        event.stopPropagation();
    }
/**
 * \@name isDropping
 * @return {?}
 */
public isDropping(): boolean {
        const /** @type {?} */ isReceiver = this.dragProvider.receiver === this;
        const /** @type {?} */ isDropping = this.dragProvider.getState('dropping');

        return Boolean(isReceiver && isDropping);
    }
/**
 * \@name onTagBlurred
 * @param {?} changedElement {TagModel}
 * @param {?} index {number}
 * @return {?}
 */
public onTagBlurred(changedElement: TagModel, index: number): void {
        this.items[index] = changedElement;
        this.blur();
    }
/**
 * \@name trackBy
 * @param {?} item
 * @return {?}
 */
public trackBy(item: TagModel): string {
        return item[this.identifyBy];
    }
/**
 *
 * @param {?} tag
 * @param {?=} fromAutocomplete
 * @return {?}
 */
private isTagValid(tag: TagModel, fromAutocomplete = false): boolean {
        const /** @type {?} */ selectedItem = this.dropdown ? this.dropdown.selectedItem : undefined;

        if (selectedItem && !fromAutocomplete) {
            return false;
        }

        const /** @type {?} */ dupe = this.findDupe(tag, fromAutocomplete);

        // if so, give a visual cue and return false
        if (!this.allowDupes && dupe && this.blinkIfDupe) {
            const /** @type {?} */ model = this.tags.find(tag => {
                return this.getItemValue(tag.model) === this.getItemValue(dupe);
            });

            if (model) {
                model.blink();
            }
        }

        const /** @type {?} */ isFromAutocomplete = fromAutocomplete && this.onlyFromAutocomplete;

        const /** @type {?} */ assertions = [
            // 1. there must be no dupe OR dupes are allowed
            !dupe || this.allowDupes === true,

            // 2. check max items has not been reached
            this.maxItemsReached === false,

            // 3. check item comes from autocomplete or onlyFromAutocomplete is false
            ((isFromAutocomplete) || this.onlyFromAutocomplete === false)
        ];

        return assertions.filter(item => item).length === assertions.length;
    }
/**
 * \@name moveToTag
 * @param {?} item
 * @param {?} direction
 * @return {?}
 */
private moveToTag(item: TagModel, direction: string): void {
        const /** @type {?} */ isLast = this.tags.last.model === item;
        const /** @type {?} */ isFirst = this.tags.first.model === item;
        const /** @type {?} */ stopSwitch = (direction === constants.NEXT && isLast) ||
            (direction === constants.PREV && isFirst);

        if (stopSwitch) {
            this.focus(true);
            return;
        }

        const /** @type {?} */ offset = direction === constants.NEXT ? 1 : -1;
        const /** @type {?} */ index = this.getTagIndex(item) + offset;
        const /** @type {?} */ tag = this.getTagAtIndex(index);

        return tag.select.call(tag);
    }
/**
 * \@name getTagIndex
 * @param {?} item
 * @return {?}
 */
private getTagIndex(item: TagModel): number {
        const /** @type {?} */ tags = this.tags.toArray();

        return tags.findIndex(tag => tag.model === item);
    }
/**
 * \@name getTagAtIndex
 * @param {?} index
 * @return {?}
 */
private getTagAtIndex(index: number) {
        const /** @type {?} */ tags = this.tags.toArray();

        return tags[index];
    }
/**
 * \@name removeItem
 * @desc removes an item from the array of the model
 * @param {?} tag {TagModel}
 * @param {?} index {number}
 * @return {?}
 */
private removeItem(tag: TagModel, index: number): void {
        this.items = this.getItemsWithout(index);

        // if the removed tag was selected, set it as undefined
        if (this.selectedTag === tag) {
            this.selectItem(undefined, false);
        }

        // focus input
        this.focus(true, false);

        // emit remove event
        this.onRemove.emit(tag);
    }
/**
 * \@name addItem
 * @desc adds the current text model to the items array
 * @param {?=} fromAutocomplete
 * @param {?=} item
 * @param {?=} index
 * @return {?}
 */
private addItem(fromAutocomplete = false, item: TagModel, index?: number): void {
        const /** @type {?} */ model = this.getItemDisplay(item);
/**
 * \@name reset
 */
const reset = (): void => {
            // reset control and focus input
            this.setInputValue('');

            // focus input
            this.focus(true, false);
        };
/**
 * \@name validationFilter
 * @param tag
 */
const validationFilter = (tag: TagModel): boolean => {
            const isValid = this.isTagValid(tag, fromAutocomplete) && this.inputForm.form.valid;

            if (!isValid) {
                this.onValidationError.emit(tag);
            }

            return isValid;
        };
/**
 * \@name subscribeFn
 * @param tag
 */
const subscribeFn = (tag: TagModel): void => {
            this.appendTag(tag, index);

            // emit event
            this.onAdd.emit(tag);

            if (!this.dropdown) {
                return;
            }

            this.dropdown.hide();
            this.dropdown.showDropdownIfEmpty ? this.dropdown.show() : undefined;
        };

        Observable
            .of(model)
            .first()
            .filter(() => model.trim() !== '')
            .map(() => item)
            .map(this.createTag)
            .filter(validationFilter)
            .subscribe(subscribeFn, undefined, reset);
    }
/**
 * \@name setupSeparatorKeysListener
 * @return {?}
 */
private setupSeparatorKeysListener(): void {
        const /** @type {?} */ useSeparatorKeys = this.separatorKeyCodes.length > 0 || this.separatorKeys.length > 0;
        const /** @type {?} */ listener = ($event) => {
            const /** @type {?} */ hasKeyCode = this.separatorKeyCodes.indexOf($event.keyCode) >= 0;
            const /** @type {?} */ hasKey = this.separatorKeys.indexOf($event.key) >= 0;

            if (hasKeyCode || hasKey) {
                $event.preventDefault();
                this.onAddingRequested(false, this.formValue);
            }
        };

        listen.call(this, constants.KEYDOWN, listener, useSeparatorKeys);
    }
/**
 * \@name setUpKeypressListeners
 * @return {?}
 */
private setUpKeypressListeners(): void {
        const /** @type {?} */ listener = ($event) => {
            const /** @type {?} */ isCorrectKey = $event.keyCode === 37 || $event.keyCode === 8;

            if (isCorrectKey &&
                !this.formValue &&
                this.items.length) {
                this.tags.last.select.call(this.tags.last);
            }
        };

        // setting up the keypress listeners
        listen.call(this, constants.KEYDOWN, listener);
    }
/**
 * \@name setUpKeydownListeners
 * @return {?}
 */
private setUpInputKeydownListeners(): void {
        this.inputForm.onKeydown.subscribe(event => {
            this.fireEvents('keydown', event);

            if (event.key === 'Backspace' && this.formValue === '') {
                event.preventDefault();
            }
        });
    }
/**
 * \@name setUpOnPasteListener
 * @return {?}
 */
private setUpOnPasteListener(): void {
        const /** @type {?} */ input = this.inputForm.input.nativeElement;

        // attach listener to input
        this.renderer.listen(input, 'paste', this.onPasteCallback);
    }
/**
 * \@name setUpTextChangeSubscriber
 * @return {?}
 */
private setUpTextChangeSubscriber(): void {
        this.inputForm.form
            .valueChanges
            .debounceTime(this.onTextChangeDebounce)
            .map(() => this.formValue)
            .subscribe((value: string) => this.onTextChange.emit(value));
    }
/**
 * \@name setUpOnBlurSubscriber
 * @return {?}
 */
private setUpOnBlurSubscriber(): void {
        const /** @type {?} */ filterFn = (): boolean => {
            return !(this.dropdown && this.dropdown.isVisible) && !!this.formValue;
        };

        this.inputForm
            .onBlur
            .filter(filterFn)
            .subscribe(() => {
                if (this.addOnBlur) {
                    this.onAddingRequested(false, this.formValue);
                }

                this.setInputValue('');
            });
    }
/**
 * \@name findDupe
 * @param {?} tag
 * @param {?} isFromAutocomplete
 * @return {?}
 */
private findDupe(tag: TagModel, isFromAutocomplete: boolean): TagModel | undefined {
        const /** @type {?} */ identifyBy = isFromAutocomplete ? this.dropdown.identifyBy : this.identifyBy;
        const /** @type {?} */ id = tag[identifyBy];

        return this.items.find(item => this.getItemValue(item) === id);
    }
/**
 * \@name onPasteCallback
 * @param data
 */
private onPasteCallback = (data: ClipboardEvent): void => {
        const text = data.clipboardData.getData('text/plain');

        text.split(this.pasteSplitPattern)
            .map(item => this.createTag(item))
            .forEach(item => this.onAddingRequested(false, item));

        this.onPaste.emit(text);

        setTimeout(() => this.setInputValue(''), 0);
    }
/**
 * \@name setAnimationMetadata
 * @return {?}
 */
private setAnimationMetadata(): void {
        this.animationMetadata = {
            value: 'in',
            params: {...this.animationDuration}
        };
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'tag-input',
    providers: [CUSTOM_ACCESSOR],
    styles: [`
      .dark tag:focus {
        -webkit-box-shadow: 0 0 0 1px #323232;
                box-shadow: 0 0 0 1px #323232; }

      .ng2-tag-input.bootstrap3-info {
        background-color: #fff;
        display: inline-block;
        color: #555;
        vertical-align: middle;
        max-width: 100%;
        height: 42px;
        line-height: 44px; }

      .ng2-tag-input.bootstrap3-info input {
        border: none;
        -webkit-box-shadow: none;
                box-shadow: none;
        outline: none;
        background-color: transparent;
        padding: 0 6px;
        margin: 0;
        width: auto;
        max-width: inherit; }

      .ng2-tag-input.bootstrap3-info .form-control input::-moz-placeholder {
        color: #777;
        opacity: 1; }

      .ng2-tag-input.bootstrap3-info .form-control input:-ms-input-placeholder {
        color: #777; }

      .ng2-tag-input.bootstrap3-info .form-control input::-webkit-input-placeholder {
        color: #777; }

      .ng2-tag-input.bootstrap3-info input:focus {
        border: none;
        -webkit-box-shadow: none;
                box-shadow: none; }

      .bootstrap3-info.ng2-tag-input.ng2-tag-input--focused {
        -webkit-box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.4);
                box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.4);
        border: 1px solid #ccc; }

      .bootstrap3-info.ng2-tag-input.ng2-tag-input--invalid {
        -webkit-box-shadow: inset 0 1px 1px #d9534f;
                box-shadow: inset 0 1px 1px #d9534f; }

      .ng2-tag-input {
        display: block;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        position: relative;
        -webkit-transition: all 0.25s;
        transition: all 0.25s;
        padding: 0.25rem 0;
        min-height: 32px;
        cursor: text;
        border-bottom: 2px solid #efefef; }
        .ng2-tag-input:focus {
          outline: 0; }
        .ng2-tag-input.ng2-tag-input--dropping {
          opacity: 0.7; }
        .ng2-tag-input.ng2-tag-input--focused {
          border-bottom: 2px solid #2196F3; }
        .ng2-tag-input.ng2-tag-input--invalid {
          border-bottom: 2px solid #f44336; }
        .ng2-tag-input.ng2-tag-input--loading {
          border: none; }
        .ng2-tag-input.ng2-tag-input--disabled {
          opacity: 0.5;
          cursor: not-allowed; }
        .ng2-tag-input form {
          margin: 0.1em 0; }
        .ng2-tag-input .ng2-tags-container {
          -ms-flex-wrap: wrap;
              flex-wrap: wrap;
          display: -webkit-box;
          display: -ms-flexbox;
          display: flex; }

      .minimal.ng2-tag-input {
        display: block;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        position: relative;
        cursor: text;
        border-bottom: 1px solid transparent; }
        .minimal.ng2-tag-input:focus {
          outline: 0; }
        .minimal.ng2-tag-input.ng2-tag-input--dropping {
          opacity: 0.7; }
        .minimal.ng2-tag-input.ng2-tag-input--loading {
          border: none; }
        .minimal.ng2-tag-input.ng2-tag-input--disabled {
          opacity: 0.5;
          cursor: not-allowed; }
        .minimal.ng2-tag-input .ng2-tags-container {
          -ms-flex-wrap: wrap;
              flex-wrap: wrap;
          display: -webkit-box;
          display: -ms-flexbox;
          display: flex; }

      .dark.ng2-tag-input {
        display: block;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        position: relative;
        cursor: text;
        border-bottom: 2px solid #444; }
        .dark.ng2-tag-input:focus {
          outline: 0; }
        .dark.ng2-tag-input.ng2-tag-input--dropping {
          opacity: 0.7; }
        .dark.ng2-tag-input.ng2-tag-input--loading {
          border: none; }
        .dark.ng2-tag-input.ng2-tag-input--disabled {
          opacity: 0.5;
          cursor: not-allowed; }
        .dark.ng2-tag-input .ng2-tags-container {
          -ms-flex-wrap: wrap;
              flex-wrap: wrap;
          display: -webkit-box;
          display: -ms-flexbox;
          display: flex; }

      .bootstrap.ng2-tag-input {
        display: block;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        position: relative;
        cursor: text;
        border-bottom: 2px solid #efefef; }
        .bootstrap.ng2-tag-input:focus {
          outline: 0; }
        .bootstrap.ng2-tag-input.ng2-tag-input--dropping {
          opacity: 0.7; }
        .bootstrap.ng2-tag-input.ng2-tag-input--focused {
          border-bottom: 2px solid #0275d8; }
        .bootstrap.ng2-tag-input.ng2-tag-input--invalid {
          border-bottom: 2px solid #d9534f; }
        .bootstrap.ng2-tag-input.ng2-tag-input--loading {
          border: none; }
        .bootstrap.ng2-tag-input.ng2-tag-input--disabled {
          opacity: 0.5;
          cursor: not-allowed; }
        .bootstrap.ng2-tag-input .ng2-tags-container {
          -ms-flex-wrap: wrap;
              flex-wrap: wrap;
          display: -webkit-box;
          display: -ms-flexbox;
          display: flex; }

      .bootstrap3-info.ng2-tag-input {
        display: block;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        position: relative;
        padding: 4px;
        cursor: text;
        -webkit-box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
                box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
        border-radius: 4px; }
        .bootstrap3-info.ng2-tag-input:focus {
          outline: 0; }
        .bootstrap3-info.ng2-tag-input.ng2-tag-input--dropping {
          opacity: 0.7; }
        .bootstrap3-info.ng2-tag-input.ng2-tag-input--invalid {
          border-bottom: 1px solid #d9534f; }
        .bootstrap3-info.ng2-tag-input.ng2-tag-input--loading {
          border: none; }
        .bootstrap3-info.ng2-tag-input.ng2-tag-input--disabled {
          opacity: 0.5;
          cursor: not-allowed; }
        .bootstrap3-info.ng2-tag-input form {
          margin: 0.1em 0; }
        .bootstrap3-info.ng2-tag-input .ng2-tags-container {
          -ms-flex-wrap: wrap;
              flex-wrap: wrap;
          display: -webkit-box;
          display: -ms-flexbox;
          display: flex; }

      .error-message {
        font-size: 0.8em;
        color: #f44336;
        margin: 0.5em 0 0 0; }

      .bootstrap .error-message {
        color: #d9534f; }

      .progress-bar, .progress-bar:before {
        height: 2px;
        width: 100%;
        margin: 0; }

      .progress-bar {
        background-color: #2196F3;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        position: absolute;
        bottom: 0; }

      .progress-bar:before {
        background-color: #82c4f8;
        content: '';
        -webkit-animation: running-progress 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        animation: running-progress 2s cubic-bezier(0.4, 0, 0.2, 1) infinite; }

      @-webkit-keyframes running-progress {
        0% {
          margin-left: 0;
          margin-right: 100%; }
        50% {
          margin-left: 25%;
          margin-right: 0; }
        100% {
          margin-left: 100%;
          margin-right: 0; } }

      @keyframes running-progress {
        0% {
          margin-left: 0;
          margin-right: 100%; }
        50% {
          margin-left: 25%;
          margin-right: 0; }
        100% {
          margin-left: 100%;
          margin-right: 0; } }

      tag {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        font-family: "Roboto", "Helvetica Neue", sans-serif;
        font-weight: 400;
        font-size: 1em;
        letter-spacing: 0.05rem;
        color: #444;
        border-radius: 16px;
        -webkit-transition: all 0.3s;
        transition: all 0.3s;
        margin: 0.1rem 0.3rem 0.1rem 0;
        padding: 0.08rem 0.45rem;
        height: 32px;
        line-height: 34px;
        background: #efefef;
        -webkit-user-select: none;
           -moz-user-select: none;
            -ms-user-select: none;
                user-select: none;
        overflow: hidden;
        outline: 0;
        cursor: pointer;
        position: relative; }
        tag:not(.readonly):not(.tag--editing):focus {
          background: #2196F3;
          color: #fff; }
        tag:not(.readonly):not(.tag--editing):active {
          background: #0d8aee;
          color: #fff; }
        tag:not(:focus):not(.tag--editing):not(:active):not(.readonly):hover {
          background: #e2e2e2;
          color: initial; }
        tag.readonly {
          cursor: default; }
        tag.readonly:focus, tag:focus {
          outline: 0; }
        tag.tag--editing {
          background-color: #fff;
          border: 1px solid #ccc;
          cursor: text; }

      .minimal tag {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        border-radius: 0;
        background: #f9f9f9;
        -webkit-user-select: none;
           -moz-user-select: none;
            -ms-user-select: none;
                user-select: none;
        overflow: hidden;
        outline: 0;
        cursor: pointer;
        position: relative; }
        .minimal tag:not(.readonly):not(.tag--editing):focus {
          background: #d0d0d0;
          color: initial; }
        .minimal tag:not(.readonly):not(.tag--editing):active {
          background: #d0d0d0;
          color: initial; }
        .minimal tag:not(:focus):not(.tag--editing):not(:active):not(.readonly):hover {
          background: #ececec; }
        .minimal tag.readonly {
          cursor: default; }
        .minimal tag.readonly:focus, .minimal tag:focus {
          outline: 0; }
        .minimal tag.tag--editing {
          cursor: text; }

      .dark tag {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        color: #f9f9f9;
        border-radius: 3px;
        background: #444;
        -webkit-user-select: none;
           -moz-user-select: none;
            -ms-user-select: none;
                user-select: none;
        overflow: hidden;
        outline: 0;
        cursor: pointer;
        position: relative; }
        .dark tag:not(.readonly):not(.tag--editing):focus {
          background: #efefef;
          color: #444; }
        .dark tag:not(:focus):not(.tag--editing):not(:active):not(.readonly):hover {
          background: #2b2b2b;
          color: #f9f9f9; }
        .dark tag.readonly {
          cursor: default; }
        .dark tag.readonly:focus, .dark tag:focus {
          outline: 0; }
        .dark tag.tag--editing {
          cursor: text; }

      .bootstrap tag {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        color: #f9f9f9;
        border-radius: 0.25rem;
        background: #0275d8;
        -webkit-user-select: none;
           -moz-user-select: none;
            -ms-user-select: none;
                user-select: none;
        overflow: hidden;
        outline: 0;
        cursor: pointer;
        position: relative; }
        .bootstrap tag:not(.readonly):not(.tag--editing):focus {
          background: #025aa5; }
        .bootstrap tag:not(.readonly):not(.tag--editing):active {
          background: #025aa5; }
        .bootstrap tag:not(:focus):not(.tag--editing):not(:active):not(.readonly):hover {
          background: #0267bf;
          color: #f9f9f9; }
        .bootstrap tag.readonly {
          cursor: default; }
        .bootstrap tag.readonly:focus, .bootstrap tag:focus {
          outline: 0; }
        .bootstrap tag.tag--editing {
          cursor: text; }

      .bootstrap3-info tag {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
            -ms-flex-direction: row;
                flex-direction: row;
        -ms-flex-wrap: wrap;
            flex-wrap: wrap;
        font-family: inherit;
        font-weight: 400;
        font-size: 95%;
        color: #fff;
        border-radius: 0.25em;
        background: #5bc0de;
        -webkit-user-select: none;
           -moz-user-select: none;
            -ms-user-select: none;
                user-select: none;
        overflow: hidden;
        outline: 0;
        cursor: pointer;
        position: relative;
        padding: .25em .6em;
        text-align: center;
        white-space: nowrap; }
        .bootstrap3-info tag:not(.readonly):not(.tag--editing):focus {
          background: #28a1c5; }
        .bootstrap3-info tag:not(.readonly):not(.tag--editing):active {
          background: #28a1c5; }
        .bootstrap3-info tag:not(:focus):not(.tag--editing):not(:active):not(.readonly):hover {
          background: #46b8da;
          color: #fff; }
        .bootstrap3-info tag.readonly {
          cursor: default; }
        .bootstrap3-info tag.readonly:focus, .bootstrap3-info tag:focus {
          outline: 0; }
        .bootstrap3-info tag.tag--editing {
          cursor: text; }

      :host {
        display: block; }
    `],
    template: `
      <!-- CONTAINER -->

      <div ngClass="ng2-tag-input {{ theme || '' }}"
           (click)="focus(true, false)"
           [attr.tabindex]="-1"

           (drop)="dragZone ? onTagDropped($event, undefined) : undefined"
           (dragenter)="dragZone ? onDragOver($event) : undefined"
           (dragover)="dragZone ? onDragOver($event) : undefined"
           (dragend)="dragZone ? dragProvider.onDragEnd() : undefined"

           [class.ng2-tag-input--dropping]="isDropping()"
           [class.ng2-tag-input--disabled]="disable"
           [class.ng2-tag-input--loading]="isLoading"
           [class.ng2-tag-input--invalid]="hasErrors()"
           [class.ng2-tag-input--focused]="isInputFocused()">

          <!-- TAGS -->
          <div class="ng2-tags-container">
              <tag *ngFor="let item of items; let i = index; trackBy: trackBy"

                   (onSelect)="selectItem(item)"
                   (onRemove)="onRemoveRequested(item, i)"
                   (onKeyDown)="handleKeydown($event, item)"
                   (onTagEdited)="onTagEdited.emit($event)"
                   (onBlur)="onTagBlurred($event, i)"
                   draggable="{{ editable }}"

                   (dragstart)="dragZone ? onDragStarted($event, item, i) : undefined"
                   (drop)="dragZone ? onTagDropped($event, i) : undefined"
                   (dragenter)="dragZone ? onDragOver($event) : undefined"
                   (dragover)="dragZone ? onDragOver($event, i) : undefined"
                   (dragleave)="dragZone ? dragProvider.onDragEnd() : undefined"

                   [attr.tabindex]="0"
                   [disabled]="disable"
                   [@animation]="animationMetadata"
                   [hasRipple]="ripple"
                   [index]="i"
                   [removable]="removable"
                   [editable]="editable"
                   [displayBy]="displayBy"
                   [identifyBy]="identifyBy"
                   [template]="!!hasCustomTemplate() ? templates.first : undefined"
                   [draggable]="dragZone"
                   [model]="item">
              </tag>

              <tag-input-form
                  (onSubmit)="onAddingRequested(false, formValue)"
                  (onBlur)="blur()"
                  (click)="dropdown ? dropdown.show() : undefined"
                  (onKeydown)="fireEvents('keydown', $event)"
                  (onKeyup)="fireEvents('keyup', $event)"

                  [(inputText)]="inputText"
                  [disabled]="disable"
                  [validators]="validators"
                  [asyncValidators]="asyncValidators"
                  [hidden]="maxItemsReached"
                  [placeholder]="items.length ? placeholder : secondaryPlaceholder"
                  [inputClass]="inputClass"
                  [inputId]="inputId"
                  [tabindex]="tabindex">
              </tag-input-form>
          </div>

          <div class="progress-bar" *ngIf="isLoading"></div>
      </div>

      <!-- ERRORS -->
      <div *ngIf="hasErrors()" class="error-messages {{ theme || '' }}">
          <p *ngFor="let error of inputForm.getErrorMessages(errorMessages)" class="error-message">
              <span>{{ error }}</span>
          </p>
      </div>

      <ng-content></ng-content>
    `,
    animations
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: DragProvider, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'separatorKeys': [{ type: Input },],
'separatorKeyCodes': [{ type: Input },],
'placeholder': [{ type: Input },],
'secondaryPlaceholder': [{ type: Input },],
'maxItems': [{ type: Input },],
'validators': [{ type: Input },],
'asyncValidators': [{ type: Input },],
'onlyFromAutocomplete': [{ type: Input },],
'errorMessages': [{ type: Input },],
'theme': [{ type: Input },],
'onTextChangeDebounce': [{ type: Input },],
'inputId': [{ type: Input },],
'inputClass': [{ type: Input },],
'clearOnBlur': [{ type: Input },],
'hideForm': [{ type: Input },],
'addOnBlur': [{ type: Input },],
'addOnPaste': [{ type: Input },],
'pasteSplitPattern': [{ type: Input },],
'blinkIfDupe': [{ type: Input },],
'removable': [{ type: Input },],
'editable': [{ type: Input },],
'allowDupes': [{ type: Input },],
'modelAsStrings': [{ type: Input },],
'trimTags': [{ type: Input },],
'inputText': [{ type: Input },],
'ripple': [{ type: Input },],
'tabindex': [{ type: Input },],
'disable': [{ type: Input },],
'dragZone': [{ type: Input },],
'onRemoving': [{ type: Input },],
'onAdding': [{ type: Input },],
'animationDuration': [{ type: Input },],
'onAdd': [{ type: Output },],
'onRemove': [{ type: Output },],
'onSelect': [{ type: Output },],
'onFocus': [{ type: Output },],
'onBlur': [{ type: Output },],
'onTextChange': [{ type: Output },],
'onPaste': [{ type: Output },],
'onValidationError': [{ type: Output },],
'onTagEdited': [{ type: Output },],
'dropdown': [{ type: ContentChild, args: [TagInputDropdown, ] },],
'templates': [{ type: ContentChildren, args: [TemplateRef, { descendants: false }, ] },],
'inputForm': [{ type: ViewChild, args: [TagInputForm, ] },],
'tags': [{ type: ViewChildren, args: [TagComponent, ] },],
'inputTextChange': [{ type: Output },],
'tabindexAttr': [{ type: HostBinding, args: ['attr.tabindex', ] },],
};
}

function TagInputComponent_tsickle_Closure_declarations() {
/** @type {?} */
TagInputComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TagInputComponent.ctorParameters;
/** @type {?} */
TagInputComponent.propDecorators;
/**
 * \@name separatorKeys
 * @desc keyboard keys with which a user can separate items
 * @type {?}
 */
TagInputComponent.prototype.separatorKeys;
/**
 * \@name separatorKeyCodes
 * @desc keyboard key codes with which a user can separate items
 * @type {?}
 */
TagInputComponent.prototype.separatorKeyCodes;
/**
 * \@name placeholder
 * @desc the placeholder of the input text
 * @type {?}
 */
TagInputComponent.prototype.placeholder;
/**
 * \@name secondaryPlaceholder
 * @desc placeholder to appear when the input is empty
 * @type {?}
 */
TagInputComponent.prototype.secondaryPlaceholder;
/**
 * \@name maxItems
 * @desc maximum number of items that can be added
 * @type {?}
 */
TagInputComponent.prototype.maxItems;
/**
 * \@name validators
 * @desc array of Validators that are used to validate the tag before it gets appended to the list
 * @type {?}
 */
TagInputComponent.prototype.validators;
/**
 * \@name asyncValidators
 * @desc array of AsyncValidator that are used to validate the tag before it gets appended to the list
 * @type {?}
 */
TagInputComponent.prototype.asyncValidators;
/**
 * - if set to true, it will only possible to add items from the autocomplete
 * \@name onlyFromAutocomplete
 * @type {?}
 */
TagInputComponent.prototype.onlyFromAutocomplete;
/**
 * \@name errorMessages
 * @type {?}
 */
TagInputComponent.prototype.errorMessages;
/**
 * \@name theme
 * @type {?}
 */
TagInputComponent.prototype.theme;
/**
 * \@name onTextChangeDebounce
 * @type {?}
 */
TagInputComponent.prototype.onTextChangeDebounce;
/**
 * - custom id assigned to the input
 * \@name id
 * @type {?}
 */
TagInputComponent.prototype.inputId;
/**
 * - custom class assigned to the input
 * @type {?}
 */
TagInputComponent.prototype.inputClass;
/**
 * - option to clear text input when the form is blurred
 * \@name clearOnBlur
 * @type {?}
 */
TagInputComponent.prototype.clearOnBlur;
/**
 * - hideForm
 * \@name clearOnBlur
 * @type {?}
 */
TagInputComponent.prototype.hideForm;
/**
 * \@name addOnBlur
 * @type {?}
 */
TagInputComponent.prototype.addOnBlur;
/**
 * \@name addOnPaste
 * @type {?}
 */
TagInputComponent.prototype.addOnPaste;
/**
 * - pattern used with the native method split() to separate patterns in the string pasted
 * \@name pasteSplitPattern
 * @type {?}
 */
TagInputComponent.prototype.pasteSplitPattern;
/**
 * \@name blinkIfDupe
 * @type {?}
 */
TagInputComponent.prototype.blinkIfDupe;
/**
 * \@name removable
 * @type {?}
 */
TagInputComponent.prototype.removable;
/**
 * \@name editable
 * @type {?}
 */
TagInputComponent.prototype.editable;
/**
 * \@name allowDupes
 * @type {?}
 */
TagInputComponent.prototype.allowDupes;
/**
 * \@description if set to true, the newly added tags will be added as strings, and not objects
 * \@name modelAsStrings
 * @type {?}
 */
TagInputComponent.prototype.modelAsStrings;
/**
 * \@name trimTags
 * @type {?}
 */
TagInputComponent.prototype.trimTags;
/**
 * \@name ripple
 * @type {?}
 */
TagInputComponent.prototype.ripple;
/**
 * \@name tabindex
 * @desc pass through the specified tabindex to the input
 * @type {?}
 */
TagInputComponent.prototype.tabindex;
/**
 * \@name disable
 * @type {?}
 */
TagInputComponent.prototype.disable;
/**
 * \@name dragZone
 * @type {?}
 */
TagInputComponent.prototype.dragZone;
/**
 * \@name onRemoving
 * @type {?}
 */
TagInputComponent.prototype.onRemoving;
/**
 * \@name onAdding
 * @type {?}
 */
TagInputComponent.prototype.onAdding;
/**
 * \@name animationDuration
 * @type {?}
 */
TagInputComponent.prototype.animationDuration;
/**
 * \@name onAdd
 * @desc event emitted when adding a new item
 * @type {?}
 */
TagInputComponent.prototype.onAdd;
/**
 * \@name onRemove
 * @desc event emitted when removing an existing item
 * @type {?}
 */
TagInputComponent.prototype.onRemove;
/**
 * \@name onSelect
 * @desc event emitted when selecting an item
 * @type {?}
 */
TagInputComponent.prototype.onSelect;
/**
 * \@name onFocus
 * @desc event emitted when the input is focused
 * @type {?}
 */
TagInputComponent.prototype.onFocus;
/**
 * \@name onFocus
 * @desc event emitted when the input is blurred
 * @type {?}
 */
TagInputComponent.prototype.onBlur;
/**
 * \@name onTextChange
 * @desc event emitted when the input value changes
 * @type {?}
 */
TagInputComponent.prototype.onTextChange;
/**
 * - output triggered when text is pasted in the form
 * \@name onPaste
 * @type {?}
 */
TagInputComponent.prototype.onPaste;
/**
 * - output triggered when tag entered is not valid
 * \@name onValidationError
 * @type {?}
 */
TagInputComponent.prototype.onValidationError;
/**
 * - output triggered when tag is edited
 * \@name onTagEdited
 * @type {?}
 */
TagInputComponent.prototype.onTagEdited;
/**
 * \@name dropdown
 * @type {?}
 */
TagInputComponent.prototype.dropdown;
/**
 * \@name template
 * @desc reference to the template if provided by the user
 * @type {?}
 */
TagInputComponent.prototype.templates;
/**
 * \@name inputForm
 * @type {?}
 */
TagInputComponent.prototype.inputForm;
/**
 * \@name selectedTag
 * @desc reference to the current selected tag
 * @type {?}
 */
TagInputComponent.prototype.selectedTag;
/**
 * \@name isLoading
 * @type {?}
 */
TagInputComponent.prototype.isLoading;
/**
 * \@name tags
 * @desc list of Element items
 * @type {?}
 */
TagInputComponent.prototype.tags;
/**
 * \@name listeners
 * @desc array of events that get fired using \@fireEvents
 * @type {?}
 */
TagInputComponent.prototype.listeners;
/**
 * \@description emitter for the 2-way data binding inputText value
 * \@name inputTextChange
 * @type {?}
 */
TagInputComponent.prototype.inputTextChange;
/**
 * \@description private variable to bind get/set
 * \@name inputTextValue
 * @type {?}
 */
TagInputComponent.prototype.inputTextValue;
/**
 * \@name animationMetadata
 * @type {?}
 */
TagInputComponent.prototype.animationMetadata;
/**
 * \@name appendTag
 * \@param tag {TagModel}
 * @type {?}
 */
TagInputComponent.prototype.appendTag;
/**
 * \@name createTag
 * \@param model
 * @type {?}
 */
TagInputComponent.prototype.createTag;
/**
 * \@name onPasteCallback
 * \@param data
 * @type {?}
 */
TagInputComponent.prototype.onPasteCallback;
/** @type {?} */
TagInputComponent.prototype.renderer;
/** @type {?} */
TagInputComponent.prototype.dragProvider;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
